import { useState, useEffect } from 'react';

// material-ui
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

// project imports
import useModulePreferences from 'hooks/useModulePreferences';

// assets
import FontSizeOutlined from '@ant-design/icons/FontSizeOutlined';

// ==============================|| HEADER CONTENT - FONT SIZE BUTTONS ||============================== //

export default function FontSizeButtons() {
  const [preferences, updatePreferences, updatePreference, getPreference, preferencesLoaded] = useModulePreferences('Global');
  const [isAnimating, setIsAnimating] = useState(false);

  const fontSizes = [15, 16, 17, 18]; // Small, Normal, Medium, Large
  const defaultFontSize = 16;
  
  // Get current font size from preferences
  const fontSize = getPreference('FontSize', defaultFontSize);

  useEffect(() => {
    if (!preferencesLoaded) {
      return;
    }

    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [preferencesLoaded, fontSize]);

  const handleToggleFontSize = async () => {
    // Find current index and cycle to next size
    const currentIndex = fontSizes.indexOf(fontSize);
    const nextIndex = (currentIndex + 1) % fontSizes.length;
    const newSize = fontSizes[nextIndex];
    
    // Set the html element's font-size so rem units scale properly
    document.documentElement.style.fontSize = `${newSize}px`;
    await updatePreference('FontSize', newSize);
    
    // Simple animation feedback
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 200);
  };

  const getSizeLabel = (size) => {
    switch(size) {
      case 15: return 'Small';
      case 16: return 'Normal';
      case 17: return 'Medium';
      case 18: return 'Large';
      default: return 'Normal';
    }
  };

  const getNextSizeLabel = () => {
    const currentIndex = fontSizes.indexOf(fontSize);
    const nextIndex = (currentIndex + 1) % fontSizes.length;
    return getSizeLabel(fontSizes[nextIndex]);
  };

    return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* Font Size Control Button */}
      <Tooltip 
        title={`${getSizeLabel(fontSize)} (${fontSize}px) - Click for ${getNextSizeLabel()}`} 
        placement="bottom"
      >
        <IconButton
          color="secondary"
          variant="light"
          onClick={handleToggleFontSize}
          sx={(theme) => ({
            color: 'text.primary',
            bgcolor: isAnimating ? 'primary.lighter' : 'transparent',
            width: 36,
            height: 36,
            '&:hover': {
              bgcolor: 'grey.100',
              ...theme.applyStyles('dark', { 
                bgcolor: 'background.default' 
              })
            },
            transition: 'all 0.2s ease-in-out'
          })}
          aria-label={`Current font size: ${fontSize}px. Click to change to ${getNextSizeLabel()}`}
        >
          <FontSizeOutlined 
            style={{ 
              fontSize: '16px',
              transform: isAnimating ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.2s ease-in-out'
            }} 
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
